import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { loginAdmin, loginCustomer, register as apiRegister, getMeAdmin, getMeCustomer } from '../api';
import { getToken, setToken, removeToken, clearAllAuth, COOKIE_KEYS } from '../utils/cookies';
import { setPendingCustomerToken, setAuthLoginInProgress } from '../api/client';
import { invalidateRequestCache } from '../utils/requestCache';
import { cacheTenantStorageLimit } from '../utils/consultorioStorageQuota';
import { purgeConsultorioFlagsOnly } from '../utils/consultorioLocalStorage';

const ROLES = { ADMIN: 'admin', DOCTOR: 'doctor', PACIENTE: 'paciente', INVITADO: 'invitado' };

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth debe usarse dentro de AuthProvider');
  return ctx;
};

export { ROLES };

function isCustomerAuthPath(path) {
  return path === '/login' || path === '/registro';
}

function isAdminAuthPath(path) {
  return path === '/admin/login';
}

export const AuthProvider = ({ children }) => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [initialized, setInitialized] = useState(false);
  const sessionGenerationRef = useRef(0);

  const bumpSessionGeneration = useCallback(() => {
    sessionGenerationRef.current += 1;
    return sessionGenerationRef.current;
  }, []);

  const isCurrentSession = useCallback((gen) => gen === sessionGenerationRef.current, []);

  const persistToken = useCallback((token, isAdmin = false) => {
    if (isAdmin) {
      removeToken(COOKIE_KEYS.AUTH_TOKEN);
      setPendingCustomerToken(null);
      setToken(COOKIE_KEYS.ADMIN_TOKEN, token);
    } else {
      removeToken(COOKIE_KEYS.ADMIN_TOKEN);
      setToken(COOKIE_KEYS.AUTH_TOKEN, token);
      setPendingCustomerToken(token);
    }
  }, []);

  const clearTokens = useCallback(() => {
    clearAllAuth();
    setPendingCustomerToken(null);
    invalidateRequestCache('');
  }, []);

  useEffect(() => {
    purgeConsultorioFlagsOnly();
    const gen = bumpSessionGeneration();
    const path = location.pathname;
    const onCustomerAuth = isCustomerAuthPath(path);
    const onAdminAuth = isAdminAuthPath(path);
    const isAdminAppRoute = path.startsWith('/admin') && !onAdminAuth;

    if (onCustomerAuth || onAdminAuth) {
      clearAllAuth();
      setPendingCustomerToken(null);
      setUser(null);
      setInitialized(true);
      return undefined;
    }

    const customerToken = getToken(COOKIE_KEYS.AUTH_TOKEN);
    const adminToken = getToken(COOKIE_KEYS.ADMIN_TOKEN);

    if (!customerToken && !adminToken) {
      setUser(null);
      setInitialized(true);
      return undefined;
    }

    /** Navegación interna en /admin/*: no repetir /me en cada clic del menú. */
    if (isAdminAppRoute && initialized && user?.rol === ROLES.ADMIN) {
      return undefined;
    }

    /** Consultorio: sesión ya cargada — no repetir /me en cada ruta interna. */
    if (path.startsWith('/consultorio') && initialized && customerToken && user?.tenant_id) {
      return undefined;
    }

    /** Tienda / cuenta cliente: evitar revalidar en cada cambio de página. */
    const isStorefrontRoute =
      !path.startsWith('/admin') && !path.startsWith('/consultorio') && path !== '/cambiar-contrasena';
    if (isStorefrontRoute && initialized && customerToken && user && user.rol !== ROLES.ADMIN) {
      return undefined;
    }

    /** Panel admin: SecurityContext gestiona la sesión; no borrar admin_token aquí. */
    if (isAdminAppRoute) {
      if (!adminToken) {
        setUser(null);
        setInitialized(true);
        return undefined;
      }
      let cancelled = false;
      (async () => {
        try {
          const { data } = await getMeAdmin();
          if (cancelled || !isCurrentSession(gen)) return;
          setUser({
            id: data.id,
            email: data.email,
            nombre: data.full_name,
            rol: data.role || ROLES.ADMIN,
            telefono: '',
          });
        } catch {
          if (cancelled || !isCurrentSession(gen)) return;
          setUser(null);
        } finally {
          if (!cancelled && isCurrentSession(gen)) setInitialized(true);
        }
      })();
      return () => {
        cancelled = true;
      };
    }

    setInitialized(false);

    const loadUser = async () => {
      try {
        const preferCustomer =
          path.startsWith('/consultorio') || path === '/cambiar-contrasena';

        if (customerToken && (preferCustomer || !adminToken)) {
          const { data } = await getMeCustomer();
          if (!isCurrentSession(gen)) return;
          const isConsultorio = !!(data.tenant_id && data.consultorio_role);
          if (isConsultorio && data.tenant_id && data.storage_limit_mb) {
            cacheTenantStorageLimit(data.tenant_id, data.storage_limit_mb);
          }
          setUser({
            id: data.id,
            email: data.email,
            nombre: data.full_name,
            rol: isConsultorio ? data.consultorio_role : ROLES.PACIENTE,
            telefono: data.phone || '',
            tenant_id: data.tenant_id || null,
            tenant_name: data.tenant_name || null,
            tenant_slug: data.tenant_slug || null,
            tenant_logo_url: data.tenant_logo_url || null,
            consultorio_role: data.consultorio_role || null,
            storage_limit_mb: data.storage_limit_mb || null,
            must_change_password: data.must_change_password || false,
          });
          return;
        }
        if (adminToken) {
          const { data } = await getMeAdmin();
          if (!isCurrentSession(gen)) return;
          setUser({
            id: data.id,
            email: data.email,
            nombre: data.full_name,
            rol: data.role || ROLES.ADMIN,
            telefono: '',
          });
        }
      } catch {
        if (!isCurrentSession(gen)) return;
        removeToken(COOKIE_KEYS.AUTH_TOKEN);
        setPendingCustomerToken(null);
        if (!adminToken) {
          removeToken(COOKIE_KEYS.ADMIN_TOKEN);
        }
        setUser(null);
      } finally {
        if (isCurrentSession(gen)) setInitialized(true);
      }
    };

    loadUser();
    return undefined;
  }, [location.pathname, bumpSessionGeneration, isCurrentSession]);

  const login = useCallback(async (email, password) => {
    const emailClean = typeof email === 'string' ? email.trim() : email;
    const passwordClean = typeof password === 'string' ? password.trim() : password;
    const gen = bumpSessionGeneration();
    clearAllAuth();
    setPendingCustomerToken(null);
    setUser(null);
    setAuthLoginInProgress(true);
    try {
      try {
        const { data } = await loginCustomer(emailClean, passwordClean);
        if (!isCurrentSession(gen)) {
          return { success: false, error: 'Inicio de sesión cancelado. Intentá de nuevo.' };
        }
        if (!data?.access_token) {
          return { success: false, error: 'El servidor no devolvió un token de sesión.' };
        }
        persistToken(data.access_token);
        const { data: me } = await getMeCustomer(data.access_token);
        if (!isCurrentSession(gen)) {
          return { success: false, error: 'Inicio de sesión cancelado. Intentá de nuevo.' };
        }
        const isConsultorio = !!(me.tenant_id && me.consultorio_role);
        if (isConsultorio && me.tenant_id && me.storage_limit_mb) {
          cacheTenantStorageLimit(me.tenant_id, me.storage_limit_mb);
        }
        const sessionUser = {
          id: me.id,
          email: me.email,
          nombre: me.full_name,
          rol: isConsultorio ? me.consultorio_role : ROLES.PACIENTE,
          telefono: me.phone || '',
          tenant_id: me.tenant_id || null,
          tenant_name: me.tenant_name || null,
          tenant_slug: me.tenant_slug || null,
          tenant_logo_url: me.tenant_logo_url || null,
          consultorio_role: me.consultorio_role || null,
          storage_limit_mb: me.storage_limit_mb || null,
          must_change_password: me.must_change_password || false,
        };
        setUser(sessionUser);
        return { success: true, user: sessionUser };
      } catch (customerErr) {
        if (customerErr.response?.status !== 401) throw customerErr;
      }
      const { data } = await loginAdmin(emailClean, passwordClean);
      if (!isCurrentSession(gen)) {
        return { success: false, error: 'Inicio de sesión cancelado. Intentá de nuevo.' };
      }
      if (!data?.access_token) {
        return { success: false, error: 'El servidor no devolvió un token de sesión.' };
      }
      persistToken(data.access_token, true);
      const { data: me } = await getMeAdmin(data.access_token);
      if (!isCurrentSession(gen)) {
        return { success: false, error: 'Inicio de sesión cancelado. Intentá de nuevo.' };
      }
      const sessionUser = {
        id: me.id,
        email: me.email,
        nombre: me.full_name,
        rol: ROLES.ADMIN,
        telefono: '',
      };
      setUser(sessionUser);
      return { success: true, user: sessionUser };
    } catch (err) {
      const msg = err.response?.data?.detail || 'Email o contraseña incorrectos';
      return { success: false, error: typeof msg === 'string' ? msg : msg[0]?.msg || 'Error al iniciar sesión' };
    } finally {
      setAuthLoginInProgress(false);
    }
  }, [persistToken, bumpSessionGeneration, isCurrentSession]);

  const register = useCallback(async (data) => {
    const gen = bumpSessionGeneration();
    setAuthLoginInProgress(true);
    try {
      const { data: res } = await apiRegister(data);
      if (!isCurrentSession(gen)) {
        return { success: false, error: 'Registro cancelado. Intentá de nuevo.' };
      }
      if (!res?.access_token) {
        return { success: false, error: 'El servidor no devolvió un token de sesión.' };
      }
      persistToken(res.access_token);
      const { data: me } = await getMeCustomer(res.access_token);
      if (!isCurrentSession(gen)) {
        return { success: false, error: 'Registro cancelado. Intentá de nuevo.' };
      }
      const sessionUser = {
        id: me.id,
        email: me.email,
        nombre: me.full_name,
        rol: data.rol || ROLES.PACIENTE,
        telefono: me.phone || '',
      };
      setUser(sessionUser);
      return { success: true, user: sessionUser };
    } catch (err) {
      const msg = err.response?.data?.detail || 'Error al registrarse';
      return { success: false, error: typeof msg === 'string' ? msg : msg[0]?.msg || 'Error al registrarse' };
    } finally {
      setAuthLoginInProgress(false);
    }
  }, [persistToken, bumpSessionGeneration, isCurrentSession]);

  const logout = useCallback(() => {
    bumpSessionGeneration();
    setUser(null);
    clearTokens();
    if (typeof window !== 'undefined') {
      window.location.replace('/login');
    }
  }, [clearTokens, bumpSessionGeneration]);

  const refreshUser = useCallback(async () => {
    const token = getToken(COOKIE_KEYS.AUTH_TOKEN);
    if (!token) return;
    try {
      const { data } = await getMeCustomer();
      const isConsultorio = !!(data.tenant_id && data.consultorio_role);
      if (isConsultorio && data.tenant_id && data.storage_limit_mb) {
        cacheTenantStorageLimit(data.tenant_id, data.storage_limit_mb);
      }
      setUser({
        id: data.id,
        email: data.email,
        nombre: data.full_name,
        rol: isConsultorio ? data.consultorio_role : ROLES.PACIENTE,
        telefono: data.phone || '',
        tenant_id: data.tenant_id || null,
        tenant_name: data.tenant_name || null,
        tenant_slug: data.tenant_slug || null,
        tenant_logo_url: data.tenant_logo_url || null,
        consultorio_role: data.consultorio_role || null,
        storage_limit_mb: data.storage_limit_mb || null,
        must_change_password: data.must_change_password || false,
      });
    } catch {
      setUser(null);
    }
  }, []);

  const isRole = (role) => user?.rol === role;
  const isAdmin = () => isRole(ROLES.ADMIN);
  const isDoctor = () => {
    if (!user?.tenant_id) return false;
    const role = user.consultorio_role || user.rol;
    return ['doctor', 'administrador', 'asistente', 'recepcionista', 'viewer'].includes(role);
  };
  const isPaciente = () => !user?.tenant_id && isRole(ROLES.PACIENTE);
  const isConsultorioUser = () => !!user?.tenant_id;

  const value = {
    user,
    initialized,
    login,
    register,
    logout,
    refreshUser,
    isAuthenticated: !!user,
    isRole,
    isAdmin,
    isDoctor,
    isPaciente,
    isConsultorioUser,
    ROLES,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
